import React, { useEffect, useState } from 'react';
import { Stack, Group, Text, Badge, Loader } from '@mantine/core';
import { supabase } from '../../lib/supabase';
import { postApi } from '../../lib/api';

const StatusBadge = ({ paid }) => (
  <Badge size="sm" variant="light" color={paid ? 'green' : 'gray'} style={{ flexShrink: 0 }}>
    {paid ? 'Paid' : 'Not paid'}
  </Badge>
);

const Row = ({ label, detail, paid }) => (
  <Group justify="space-between" wrap="nowrap" gap="sm">
    <div style={{ minWidth: 0, textAlign: 'left' }}>
      <Text size="sm" weight={600} style={{ color: 'var(--charcoal)', overflowWrap: 'anywhere' }}>{label}</Text>
      {detail && <Text size="xs" color="dimmed">{detail}</Text>}
    </div>
    <StatusBadge paid={paid} />
  </Group>
);

/**
 * The profile's "Your entries" list for a 2027+ registration: each solo,
 * and ensemble with whether it's paid. Solos come from
 * `variations` (readable by the owner); ensembles from /api/ensemble.
 */
const EntriesSummary = ({ registration }) => {
  const [solos, setSolos] = useState(null);
  const [ensembles, setEnsembles] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    supabase.from('variations')
      .select('variation_number, variation_name, ballet_origin, paid_payment_id')
      .eq('registration_id', registration.id)
      .order('variation_number')
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) { setLoadError(true); return; }
        setSolos(data || []);
      });
    postApi('/api/ensemble', { action: 'status', registrationId: registration.id })
      .then((data) => { if (!cancelled) setEnsembles(data.entries || []); })
      .catch(() => { if (!cancelled) setLoadError(true); });
    return () => { cancelled = true; };
  }, [registration.id]);

  if (loadError) {
    return (
      <Text size="sm" style={{ textAlign: 'left', color: '#c92a2a' }}>
        We couldn't load your entries. Please refresh the page to try again.
      </Text>
    );
  }
  if (!solos || !ensembles) return <Group justify="center" py="xs"><Loader size="sm" /></Group>;

  const anyUnpaid = solos.some((s) => !s.paid_payment_id) || ensembles.some((e) => !e.paid);

  if (!solos.length && !ensembles.length) {
    return <Text size="sm" color="dimmed" style={{ textAlign: 'left' }}>No entries yet.</Text>;
  }

  return (
    <Stack gap="sm">
      {solos.map((s) => (
        <Row
          key={`s${s.variation_number}`}
          label={`Solo — ${s.variation_name || 'piece not chosen yet'}`}
          detail={s.ballet_origin || null}
          paid={Boolean(s.paid_payment_id)}
        />
      ))}
      {ensembles.map((e) => (
        <Row
          key={e.entryId}
          label={`Ensemble — ${e.team ? e.team.name : 'not on a team yet'}`}
          detail={e.team
            ? `${e.team.members.length} dancer${e.team.members.length === 1 ? '' : 's'}${e.team.finalizedAt ? ' · Finalized' : ''}`
            : 'Join a team any time before registration closes'}
          paid={e.paid}
        />
      ))}
      {anyUnpaid && (
        <Text size="xs" style={{ textAlign: 'left', color: 'var(--deep-gold)' }}>
          Some entries aren't paid yet — use “Add or Edit Entries” to check out.
        </Text>
      )}
    </Stack>
  );
};

export default EntriesSummary;
